import { useEffect, useRef } from 'react'
import { useAppStore } from '../stores/appStore'
import { streamRun, runsApi } from '../utils/api'
import { TaskPanel } from './TaskPanel'
import type { StartEvent, AgentProgressEvent, AgentCompleteEvent, SearchActivityEvent, CompleteEvent, ErrorEvent } from '../types'

/**
 * RunnerModal - Handles SSE streaming and recovery logic, renders TaskPanel.
 *
 * This component:
 * 1. Starts SSE stream when a run is initiated
 * 2. Handles the start event to capture run_id for persistence
 * 3. Checks for active runs on mount (recovery)
 * 4. Renders the TaskPanel for UI
 */
export function RunnerModal() {
  const {
    runner,
    setRunnerRunId,
    updateRunnerStage,
    completeRunnerAgent,
    addSearchActivity,
    setRunnerResult,
    setRunnerError,
    closeRunner,
    minimizeRunner,
    checkForActiveRun,
    clearActiveRun,
    restoreRunner,
  } = useAppStore()

  const cleanupRef = useRef<(() => void) | null>(null)
  const streamKeyRef = useRef<string | null>(null)
  const hasCheckedRecovery = useRef(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Recovery logic: check for active run on mount
  useEffect(() => {
    if (hasCheckedRecovery.current) return
    hasCheckedRecovery.current = true

    const activeRun = checkForActiveRun()
    if (!activeRun) return

    // Found an active run in storage - poll its status
    const pollRunStatus = async () => {
      try {
        const runData = await runsApi.get(activeRun.runId)

        if (runData.status === 'completed') {
          // Run finished while user was away
          const result = runData.consensus ? {
            run_id: runData.id,
            final_prob: runData.consensus.post_market_prob,
            confidence: 0.8, // Default confidence since it's not stored
            decision: 'hold', // Would need to re-derive
          } : undefined

          if (result) {
            restoreRunner(activeRun, result)
          } else {
            // No consensus data, just clear it
            clearActiveRun()
          }
        } else {
          // Still running - show minimized indicator and poll
          restoreRunner(activeRun)
          startPolling(activeRun.runId)
        }
      } catch {
        // Run not found or error - clear storage
        clearActiveRun()
      }
    }

    pollRunStatus()
  }, [])

  // Start polling for run completion
  const startPolling = (runId: number) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }

    pollingRef.current = setInterval(async () => {
      try {
        const runData = await runsApi.get(runId)

        if (runData.status === 'completed') {
          // Run finished
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }

          if (runData.consensus) {
            setRunnerResult({
              run_id: runData.id,
              final_prob: runData.consensus.post_market_prob,
              confidence: 0.8,
              decision: 'hold',
            })
          } else {
            setRunnerError('Run completed but no consensus found')
          }
        }
      } catch (err) {
        // Error polling - stop and show error
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        setRunnerError('Lost connection to run')
      }
    }, 5000) // Poll every 5 seconds
  }

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  // Start SSE stream when runner opens with a new run
  useEffect(() => {
    if (!runner.isOpen || !runner.questionId || !runner.isRunning) return

    // Avoid restarting the EventSource when run_id gets set after the 'start' event.
    const streamKey = `${runner.questionId}:${runner.paperTrade ? 'paper' : 'live'}`
    if (streamKeyRef.current === streamKey) return
    streamKeyRef.current = streamKey

    // Clean up any existing stream
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }

    const cleanup = streamRun(
      runner.questionId,
      (event) => {
        switch (event.type) {
          case 'start':
            // Capture run_id for persistence
            const start = event.data as StartEvent
            setRunnerRunId(start.run_id, start.started_at)
            break

          case 'agent_progress':
            updateRunnerStage(event.data as AgentProgressEvent)
            break

          case 'agent_complete':
            // Rich event with probability, direction, key findings
            completeRunnerAgent(event.data as AgentCompleteEvent)
            break

          case 'search_activity':
            // Live search feed
            addSearchActivity(event.data as SearchActivityEvent)
            break

          case 'complete':
            const complete = event.data as CompleteEvent
            setRunnerResult({
              run_id: complete.run_id,
              final_prob: complete.final_prob,
              confidence: complete.confidence,
              decision: complete.decision,
              trade: complete.trade,
            })
            break

          case 'error':
            const err = event.data as ErrorEvent
            setRunnerError(err.message)
            break
        }
      },
      (error) => {
        // Connection error - if we have a run_id, we can try polling
        if (runner.runId) {
          startPolling(runner.runId)
        } else {
          setRunnerError(error.message)
        }
      },
      runner.paperTrade
    )

    cleanupRef.current = cleanup

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
      streamKeyRef.current = null
    }
  }, [runner.isOpen, runner.questionId, runner.isRunning, runner.paperTrade])

  // Handle escape key - minimize when running, close when not
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && runner.isOpen) {
        if (runner.isRunning) {
          minimizeRunner()
        } else {
          closeRunner()
        }
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [runner.isOpen, runner.isRunning, closeRunner, minimizeRunner])

  // Render the TaskPanel
  return <TaskPanel />
}
